import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Flag, PlayCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useFPContext } from '../../context/FuturePathContext';
import celebrate from '../../utils/celebrate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGE_KEY = 'futurepath_stages_done';

const PRAISE = [
  'Good to see you back. You are further along than most people ever get.',
  'You are doing great, momentum like this is the whole game.',
  'Back at it. Every visit moves this forward.',
  'Nice work so far. Let us keep the streak going.',
  'You are building something real here. Keep going.',
];

/**
 * The dashboard arrival: their name, the stage they're on, how far along the
 * journey is, and the one thing to do next. Finishing a stage is celebrated once.
 */
export const WelcomeHeader = ({ firstVisit }) => {
  const { user, getAuthHeader } = useAuth();
  const { ready, stageLabel, age, archetype, known } = useFPContext();
  const [d, setD] = useState(null);
  const [school, setSchool] = useState(null);
  const [cost, setCost] = useState(null);
  // A district (B2B) high school student is already in a path, the district put them there.
  // A self-signup (B2C) family still chooses, so they keep this control.
  const hsGrade = parseInt(user?.grade || user?.grade_level, 10);
  const isDistrictHighSchool = !!(user?.organization_id || user?.org_id)
    && hsGrade >= 9 && hsGrade <= 12;
  const celebrated = useRef(false);

  useEffect(() => {
    axios.get(`${API}/journey/mission-control`, getAuthHeader()).then(r => setD(r.data)).catch(() => {});
    axios.get(`${API}/nc/school-plan-summary`, getAuthHeader()).then(r => setSchool(r.data)).catch(() => {});
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cost-model/summary`, getAuthHeader()).then(r => setCost(r.data)).catch(() => {});
  }, [getAuthHeader]);

  const stages = d?.stages || [];
  const doneCount = stages.filter(s => s.status === 'done').length;

  // A light welcome-back moment on every arrival at the dashboard.
  const greeted = useRef(false);
  useEffect(() => {
    if (!d || greeted.current) return;
    greeted.current = true;
    celebrate(PRAISE[Math.floor(Math.random() * PRAISE.length)], { pieces: 14 });
  }, [d]);

  useEffect(() => {
    if (!stages.length || celebrated.current) return;
    const key = `${STAGE_KEY}_${user?.id || 'me'}`;
    const seen = Number(localStorage.getItem(key) || 0);
    if (doneCount > seen) {
      celebrated.current = true;
      localStorage.setItem(key, String(doneCount));
      if (seen > 0 || doneCount > 0) {
        const last = [...stages].reverse().find(s => s.status === 'done');
        celebrate(doneCount === stages.length
          ? 'Every stage of your journey is done. Time to make it happen.'
          : `${last?.label || 'A stage'} is done, ${doneCount} of ${stages.length} behind you.`);
      }
    }
  }, [doneCount, stages, user]);

  const name = d?.first_name || (user?.full_name || '').split(' ')[0] || 'there';
  const nba = d?.next_best_action;
  const current = stages.find(s => s.status === 'current');
  const pct = stages.length ? Math.round((doneCount / stages.length) * 100) : 0;
  const targetTitle = known('target_career');

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
      className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-[#0a1729]/60 p-7 md:p-9 mb-7"
      style={{ boxShadow: '0 40px 120px -70px rgba(34,211,238,0.7)' }} data-testid="dashboard-welcome">
      <div className="absolute -top-28 -right-20 w-[24rem] h-[24rem] rounded-full blur-3xl opacity-25"
        style={{ background: 'radial-gradient(circle, #22d3ee, transparent 65%)' }} />
      <div className="absolute -bottom-32 -left-16 w-[22rem] h-[22rem] rounded-full blur-3xl opacity-20"
        style={{ background: 'radial-gradient(circle, #a78bfa, transparent 65%)' }} />

      <div className="relative">
        <div className="flex flex-wrap items-center gap-2.5 mb-1">
          <p className="text-[11px] uppercase tracking-[0.24em] text-cyan-300/90">
            {current ? current.label : 'Your FuturePath'}
          </p>
          {(user?.grade || user?.grade_level) && (() => {
            const g = parseInt(user.grade || user.grade_level);
            const label = g === 9 ? 'Freshman' : g === 10 ? 'Sophomore' : g === 11 ? 'Junior' : g === 12 ? 'Senior' : `Grade ${g}`;
            return (
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold
                border border-violet-400/30 bg-violet-400/[0.1] text-violet-300">
                {label} · Grade {g}
              </span>
            );
          })()}
          {school?.school_name && (
            <span data-testid="welcome-school-chip"
              className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold
                border border-cyan-400/30 bg-cyan-400/[0.1] text-cyan-200">
              {school.school_name}
            </span>
          )}
          {school?.counselor_name && (
            <span data-testid="welcome-counselor-chip"
              className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold
                border border-emerald-400/30 bg-emerald-400/[0.1] text-emerald-200">
              Counselor · {school.counselor_name}
            </span>
          )}
          {user?.career_aspirations && (
            <Link to="/app/careers" data-testid="welcome-career-chip"
              className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold border border-violet-400/30
                bg-violet-400/[0.1] text-violet-200 hover:bg-violet-400/[0.18] transition-colors">
              Goal · {user.career_aspirations}
            </Link>
          )}
          {cost?.started && cost?.required_gross > 0 && (
            <Link to="/app/calculator" data-testid="welcome-cost-chip"
              className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold border border-sky-400/30
                bg-sky-400/[0.1] text-sky-200 hover:bg-sky-400/[0.18] transition-colors">
              Life costs ${Math.round(cost.required_gross / 1000)}k/yr
            </Link>
          )}
        </div>
        <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-white tracking-tight mt-2.5">
          {firstVisit ? 'Welcome' : 'Welcome back'}, {name}.
        </h1>

        {d && (
          <p className="text-base text-slate-300/90 mt-3 max-w-2xl" data-testid="dashboard-welcome-line">
            {nba?.headline ? `${nba.headline}.` : "Here's what matters most right now."}
          </p>
        )}

        {/* The context line: FuturePath says out loud who it thinks you are, so you can correct
            it in one click instead of wondering why a page asked the wrong question. */}
        {ready && (
          <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm"
            data-testid="dashboard-context-line">
            <span className="text-slate-300">Right now FuturePath is working with you as</span>
            <span className="text-white font-medium">
              {archetype?.primary?.label || 'someone still telling us their story'}
            </span>
            <span className="text-slate-300">·</span>
            <span className="text-slate-300">{stageLabel}</span>
            {typeof age === 'number' && <><span className="text-slate-300">·</span><span className="text-slate-300">{age}</span></>}
            {targetTitle && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-slate-300">aiming at</span>
                <span className="text-white font-medium">{targetTitle}</span>
              </>
            )}
            {!isDistrictHighSchool && (
              <Link to="/app/choose-path" data-testid="update-my-situation"
                className="ml-1 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs text-cyan-200
                           border border-cyan-400/30 bg-cyan-500/[0.08] hover:bg-cyan-500/20 transition-colors">
                <RefreshCw className="w-3 h-3" />Update my situation
              </Link>
            )}
          </div>
        )}

        {stages.length > 0 && (
          <div className="mt-6 max-w-xl" data-testid="dashboard-journey-progress">
            <div className="flex items-center justify-between text-[11px] text-slate-300 mb-1.5">
              <span>
                You've opened {doneCount} of the {stages.length} stages of your FuturePath
                {pct === 100 && <span className="text-slate-300">, the work inside them keeps going</span>}
              </span>
              <span className="fp-num">{pct}%</span>
            </div>
            <div className="fp-meter">
              <div className="fp-meter-fill transition-all duration-700"
                style={{ width: `${Math.max(pct, 3)}%`, background: 'linear-gradient(90deg,#22d3ee,#a78bfa)' }} />
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mt-6">
          {d?.resume && (
            <Link to={d.resume.href} data-testid="welcome-resume"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white
                         bg-gradient-to-r from-cyan-500 to-violet-500 hover:opacity-95 transition-opacity">
              <Sparkles className="w-4 h-4" />Pick up where you left off, {d.resume.label}
            </Link>
          )}
          <Link to="/app/welcome" data-testid="welcome-replay-link"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm text-slate-200
                       border border-cyan-400/30 bg-cyan-500/[0.08] hover:bg-cyan-500/15 transition-colors">
            <PlayCircle className="w-4 h-4 text-cyan-300" />My journey walkthrough
          </Link>
          {nba?.href && (
            <Link to={nba.href} data-testid="welcome-next-action"
              className="inline-flex items-center gap-2 text-sm text-cyan-300 hover:gap-3 transition-all">
              <Flag className="w-4 h-4" />Do this next <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default WelcomeHeader;
